'use client'

import {type FormProps} from './types'
import styles from './styles.module.css'
import {HgTextInput} from '~/design-system/hg/components'
import {type CleaveOptions} from 'cleave.js/options'
import {dollarsPrettifier} from '~/utils/dollarsPrettifier'

export default function InputForm({
  onChange = () => {},
  formState,
  totalTransactionsLimit,
  mercuryACHTransactionFee,
  isFormDisabled,
}: FormProps) {
  const cleaveOptions: CleaveOptions = {
    numeral: true,
    numeralPositiveOnly: true,
    numeralDecimalScale: 0,
  }

  return (
    <div className="px-s6 py-s5 text-text-default md:px-s5">
      <h2 className="arcadia-heading-9">
        We’ve prefilled some example transaction data. Adjust the inputs to reflect
        your business’s monthly activity.
      </h2>
      <form
        className="flex flex-col gap-y-s3 pt-s4 *:mt-s2 *:max-w-full"
        onSubmit={e => {
          e.preventDefault()
        }}
      >
        <HgTextInput
          label="Total transactions"
          suffix="qty. per month"
          tooltip={{
            content:
              'Enter the estimated number of incoming and outgoing transactions your business makes every month. This includes card, wire, ACH, and check transactions.',
            variant: 'rich',
          }}
          name="totalTransactions"
          cleaveOptions={cleaveOptions}
          onChange={onChange}
          value={formState.totalTransactions ?? undefined}
          disabled={isFormDisabled}
        />
        <HgTextInput
          label="How many of those transactions are ACH?"
          suffix="qty. per month"
          tooltip={{
            content: `On Mercury, the first ${dollarsPrettifier(totalTransactionsLimit, false, 0)} ACH transactions per month are free; subsequent transactions cost ${dollarsPrettifier(mercuryACHTransactionFee, true, 0)} each`,
            variant: 'rich',
          }}
          name="achTransactions"
          cleaveOptions={cleaveOptions}
          onChange={onChange}
          value={formState.achTransactions ?? undefined}
          wrapperProps={{
            'aria-hidden':
              (formState.totalTransactions || 0) <= totalTransactionsLimit,
            'className': styles.conditionalField,
          }}
          disabled={isFormDisabled}
        />
        <HgTextInput
          label="Incoming domestic wires (USD)"
          suffix="qty. per month"
          name="incomingDomesticWires"
          cleaveOptions={cleaveOptions}
          onChange={onChange}
          value={formState.incomingDomesticWires ?? undefined}
          disabled={isFormDisabled}
        />
        <HgTextInput
          label="Outgoing domestic wires (USD)"
          suffix="qty. per month"
          name="outgoingDomesticWires"
          cleaveOptions={cleaveOptions}
          onChange={onChange}
          value={formState.outgoingDomesticWires ?? undefined}
          disabled={isFormDisabled}
        />
        <HgTextInput
          label="Incoming international wires (USD)"
          suffix="qty. per month"
          name="incomingInternationalWires"
          cleaveOptions={cleaveOptions}
          onChange={onChange}
          value={formState.incomingInternationalWires ?? undefined}
          disabled={isFormDisabled}
        />
        <HgTextInput
          label="Outgoing international wires (USD)"
          suffix="qty. per month"
          name="outgoingInternationalWires"
          cleaveOptions={cleaveOptions}
          onChange={onChange}
          value={formState.outgoingInternationalWires ?? undefined}
          disabled={isFormDisabled}
        />
        <HgTextInput
          label="Outgoing international wires (non-USD)"
          suffix="amt. per month"
          prefix="$"
          name="foreignCurrencyInternationalWires"
          tooltip={{
            content:
              'Enter the USD equivalent of your monthly outgoing non-USD (FX) international wires. Note that receiving incoming international wires in non-USD is free on Mercury.',
            variant: 'rich',
          }}
          cleaveOptions={cleaveOptions}
          onChange={onChange}
          value={formState.foreignCurrencyInternationalWires ?? undefined}
          disabled={isFormDisabled}
        />
      </form>
    </div>
  )
}
